/**
 * Configuration — Flag — catalogues, compatibility, sample rules.
 * Platform-wide Flag Rules: Flag Source + Module + Target Type scope.
 * Security targets / Issues remain deferred (Section 14) until definitions are confirmed.
 */

export const FLAG_ROUTE = '/admin/configuration/flag';

export const FLAG_SOURCE_OPTIONS = [
  { value: 'user_flag', label: 'User Flag' },
  { value: 'system_flag', label: 'System Flag' },
];

export const FLAG_MODULE_OPTIONS = [
  { value: 'posts', label: 'Posts' },
  { value: 'messages', label: 'Messages' },
  { value: 'quotes', label: 'Quotes' },
  { value: 'reviews', label: 'Reviews' },
  { value: 'business_profiles', label: 'Business Profiles' },
  { value: 'accounts', label: 'Accounts', definitionsConfirmed: false },
];

export const FLAG_TARGET_TYPE_OPTIONS = [
  {
    value: 'post',
    label: 'Post',
    module: 'posts',
    userFlag: true,
    systemFlag: true,
    caseModule: 'moderation',
    concern: 'content',
    blockSelected: false,
    definitionsConfirmed: true,
  },
  {
    value: 'message_thread',
    label: 'Received Message Thread',
    module: 'messages',
    userFlag: true,
    systemFlag: false,
    caseModule: 'moderation',
    concern: 'content',
    blockSelected: true,
    definitionsConfirmed: true,
  },
  {
    value: 'quote_thread',
    label: 'Received Quote Thread',
    module: 'quotes',
    userFlag: true,
    systemFlag: false,
    caseModule: 'moderation',
    concern: 'content',
    blockSelected: false,
    definitionsConfirmed: true,
  },
  {
    value: 'public_review_or_reply',
    label: 'Public Review or Business Reply',
    module: 'reviews',
    userFlag: false,
    systemFlag: true,
    caseModule: 'moderation',
    concern: 'content',
    blockSelected: false,
    definitionsConfirmed: true,
  },
  {
    value: 'business_profile_item',
    label: 'Public Business Profile item',
    module: 'business_profiles',
    userFlag: false,
    systemFlag: true,
    caseModule: 'moderation',
    concern: 'content',
    blockSelected: false,
    definitionsConfirmed: true,
  },
  {
    value: 'affected_account',
    label: 'Affected Customer or Business account',
    module: 'accounts',
    userFlag: false,
    systemFlag: true,
    caseModule: 'fraud_security',
    concern: 'security',
    blockSelected: false,
    definitionsConfirmed: false,
  },
];

export const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

export const CASE_MODULE_OPTIONS = [
  { value: 'moderation', label: 'Moderation', receivingAvailable: true },
  /**
   * Fraud & Security receiving mappings remain NOT CONFIRMED (Section 14).
   * Content Flags never use this destination; security Flags cannot Save until ready.
   */
  { value: 'fraud_security', label: 'Fraud & Security', receivingAvailable: false },
];

/** Section 14 — deferred definition areas. Not selectable fields. */
export const FLAG_DEFERRED_DEFINITIONS = [
  {
    id: 'submission_abuse_thresholds',
    label: 'Submission abuse thresholds',
    status: 'NOT DEFINED',
    detail: 'Submission counts, time periods, pause duration and further-attempt thresholds belong in Limits & Thresholds.',
  },
  {
    id: 'login_risk_findings',
    label: 'Supported login-risk findings and required evidence',
    status: 'NOT CONFIRMED',
    detail: 'Login-risk findings and their required evidence are not confirmed.',
  },
  {
    id: 'security_targets_issues',
    label: 'Security targets, Issues, Conditions and evidence relationships',
    status: 'NOT CONFIRMED',
    detail: 'Including reliable identification of the same ongoing attack.',
  },
  {
    id: 'fs_case_mappings',
    label: 'F&S case Source and Type mappings for new security Flags',
    status: 'NOT CONFIRMED',
    detail: 'Receiving case definitions remain outstanding.',
  },
];

export const FLAG_READINESS_BANNER =
  'NOT YET READY — security Flag definitions in Section 14 remain outstanding. Content Flag Rules can be saved; security targets cannot.';

export function isCaseModuleReceivingAvailable(caseModule) {
  const found = CASE_MODULE_OPTIONS.find((o) => o.value === caseModule);
  return Boolean(found?.receivingAvailable);
}

export const ADDITIONAL_DETAILS_OPTIONS = [
  { value: 'optional', label: 'Optional' },
  { value: 'required', label: 'Required' },
];

export const CONDITION_OPERATOR_OPTIONS = [
  { value: 'equals', label: 'Equals', multi: false },
  { value: 'does_not_equal', label: 'Does Not Equal', multi: false },
  { value: 'is_any_of', label: 'Is Any Of', multi: true },
  { value: 'is_not_any_of', label: 'Is Not Any Of', multi: true },
];

export const SUBMITTER_TYPE_OPTIONS = [
  { value: 'customer', label: 'Customer' },
  { value: 'business', label: 'Business' },
];

export const BLOCK_SELECTED_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

export const MATCH_CLASSIFICATION_OPTIONS = [
  { value: 'confirmed_match', label: 'Confirmed Match' },
  { value: 'suspected_match', label: 'Suspected Match' },
];

export const CONTENT_AREA_OPTIONS = [
  { value: 'post_title', label: 'Post Title', targetTypes: ['post'] },
  { value: 'post_description', label: 'Post Description', targetTypes: ['post'] },
  { value: 'post_image', label: 'Post Image', targetTypes: ['post'] },
  { value: 'review_content', label: 'Review Content', targetTypes: ['public_review_or_reply'] },
  { value: 'business_reply', label: 'Business Reply', targetTypes: ['public_review_or_reply'] },
  { value: 'profile_description', label: 'Profile Description', targetTypes: ['business_profile_item'] },
  { value: 'portfolio_image', label: 'Portfolio Image', targetTypes: ['business_profile_item'] },
  { value: 'profile_image', label: 'Profile Image', targetTypes: ['business_profile_item'] },
];

export const CONTENT_CONTEXT_OPTIONS = [
  { value: 'public_content', label: 'Public Content', targetTypes: ['post', 'public_review_or_reply', 'business_profile_item'] },
  { value: 'published_content', label: 'Published Content', targetTypes: ['post', 'public_review_or_reply', 'business_profile_item'] },
];

export const CHECK_CONTEXT_OPTIONS = [
  { value: 'live_publish', label: 'Live Publish', targetTypes: ['post', 'public_review_or_reply', 'business_profile_item'] },
  { value: 'existing_content_recheck', label: 'Existing-Content-Recheck', targetTypes: ['post', 'public_review_or_reply', 'business_profile_item'] },
];

/** Compatible User Flag reasons by target type. */
const REASONS_BY_TARGET = {
  post: [
    { value: 'spam', label: 'Spam' },
    { value: 'misleading', label: 'Misleading Content' },
    { value: 'scam', label: 'Scam or Suspicious Content' },
    { value: 'offensive', label: 'Offensive Content' },
    { value: 'copyright', label: 'Copyright Issues' },
    { value: 'profanity', label: 'Profanity Issues' },
    { value: 'other', label: 'Other' },
  ],
  message_thread: [
    { value: 'abuse', label: 'Abuse' },
    { value: 'scam', label: 'Scam' },
    { value: 'spam', label: 'Spam' },
    { value: 'contact_misuse', label: 'Contact Misuse' },
    { value: 'other', label: 'Other' },
  ],
  quote_thread: [
    { value: 'spam', label: 'Spam' },
    { value: 'scam', label: 'Scam or Suspicious Content' },
    { value: 'misleading', label: 'Misleading Content' },
    { value: 'contact_misuse', label: 'Contact Misuse' },
    { value: 'other', label: 'Other' },
  ],
};

/** Compatible System Flag issues by target type (content only; security deferred). */
const ISSUES_BY_TARGET = {
  post: [
    { value: 'spam_or_unwanted_promotion', label: 'Spam or Unwanted Promotion', definitionsConfirmed: true },
    { value: 'inappropriate_language', label: 'Inappropriate Language', definitionsConfirmed: true },
    { value: 'abuse_or_harassment', label: 'Abuse or Harassment', definitionsConfirmed: true },
    { value: 'scam_fraud_or_phishing', label: 'Scam, Fraud or Phishing', definitionsConfirmed: true },
    { value: 'unsafe_image', label: 'Unsafe Image', definitionsConfirmed: true },
  ],
  public_review_or_reply: [
    { value: 'review_integrity_concern', label: 'Review Integrity Concern', definitionsConfirmed: true },
    { value: 'inappropriate_language', label: 'Inappropriate Language', definitionsConfirmed: true },
    { value: 'abuse_or_harassment', label: 'Abuse or Harassment', definitionsConfirmed: true },
    { value: 'spam_or_unwanted_promotion', label: 'Spam or Unwanted Promotion', definitionsConfirmed: true },
  ],
  business_profile_item: [
    { value: 'direct_or_off_platform_contact', label: 'Direct or Off-Platform Contact', definitionsConfirmed: true },
    { value: 'impersonation_or_misrepresentation', label: 'Impersonation or Misrepresentation', definitionsConfirmed: true },
    { value: 'unsafe_image', label: 'Unsafe Image', definitionsConfirmed: true },
    { value: 'spam_or_unwanted_promotion', label: 'Spam or Unwanted Promotion', definitionsConfirmed: true },
  ],
  affected_account: [
    { value: 'serious_repeated_login_attack', label: 'Serious repeated login attack', definitionsConfirmed: false, severity: 'medium' },
    { value: 'suspected_account_takeover', label: 'Suspected account takeover', definitionsConfirmed: false, severity: 'high' },
    { value: 'repeated_malicious_upload', label: 'Repeated malicious upload', definitionsConfirmed: false, severity: 'medium' },
    { value: 'malicious_file_exposed', label: 'Malicious file exposed', definitionsConfirmed: false, severity: 'high' },
    { value: 'repeated_submission_abuse', label: 'Repeated submission abuse', definitionsConfirmed: false, severity: 'medium' },
  ],
};

const CONTEXT_CAPTURE_BY_TARGET = {
  post: 'Reported Post content and captured version.',
  message_thread: 'Full captured thread and exact flagged messages.',
  quote_thread: 'Full captured thread and exact flagged messages or supported request card.',
  public_review_or_reply: 'Exact Review and relevant reply context, or exact reply and relevant parent Review.',
  business_profile_item: 'Exact affected item and relevant profile context.',
  affected_account: 'Affected account, exact incident and the evidence required by its confirmed source definition.',
};

const ELIGIBILITY_BY_SCOPE = {
  user_flag: 'Eligible verified Customer or Business with the applicable access and reporting relationship; one compatible Reason; exact reported target and captured content; Additional Details when required.',
  system_flag_content: 'Completed Content Control result identifying the Issue, target, classification, evidence and captured context. Confirmed Match and Suspected Match are eligible; No Match and incomplete results are not.',
  system_flag_security: 'Required Flag information is not defined or available. Security qualification remains deferred until Section 14 definitions are confirmed.',
};

const EVIDENCE_BY_SCOPE = {
  user_flag: 'Supporting Uploads: optional images and files; no video or audio. Optional uploads do not replace required captured content.',
  system_flag_content: 'Completed finding with classification, supporting evidence and captured context from Content Control.',
  system_flag_security: 'Security evidence requirements remain deferred until source definitions are confirmed.',
};

export function labelFromOptions(options, value) {
  if (value == null || value === '') return '';
  const found = (options || []).find((o) => o.value === value);
  return found ? found.label : String(value);
}

export function operatorIsMulti(operator) {
  return Boolean(CONDITION_OPERATOR_OPTIONS.find((o) => o.value === operator)?.multi);
}

export function getTargetTypeDef(targetType) {
  return FLAG_TARGET_TYPE_OPTIONS.find((o) => o.value === targetType) || null;
}

export function getFlagSourceLabel(value) {
  return labelFromOptions(FLAG_SOURCE_OPTIONS, value);
}

export function getFlagModuleLabel(value) {
  return labelFromOptions(FLAG_MODULE_OPTIONS, value);
}

export function getTargetTypeLabel(value) {
  return labelFromOptions(FLAG_TARGET_TYPE_OPTIONS, value);
}

export function getSeverityLabel(value) {
  return labelFromOptions(SEVERITY_OPTIONS, value);
}

export function getCaseModuleLabel(value) {
  return labelFromOptions(CASE_MODULE_OPTIONS, value);
}

export function getAdditionalDetailsLabel(value) {
  return labelFromOptions(ADDITIONAL_DETAILS_OPTIONS, value);
}

export function getConditionOperatorLabel(value) {
  return labelFromOptions(CONDITION_OPERATOR_OPTIONS, value);
}

export function getModulesForFlagSource(flagSource) {
  if (!flagSource) return [];
  const modules = new Set(
    FLAG_TARGET_TYPE_OPTIONS.filter((t) => (flagSource === 'user_flag' ? t.userFlag : t.systemFlag)).map((t) => t.module),
  );
  return FLAG_MODULE_OPTIONS.filter((m) => modules.has(m.value));
}

export function getTargetTypesForScope(flagSource, module) {
  if (!flagSource || !module) return [];
  return FLAG_TARGET_TYPE_OPTIONS.filter((t) => {
    if (t.module !== module) return false;
    return flagSource === 'user_flag' ? t.userFlag : t.systemFlag;
  });
}

export function getPermittedCaseModule(flagSource, targetType) {
  const def = getTargetTypeDef(targetType);
  if (!def) return '';
  return def.caseModule || '';
}

export function isSecurityTarget(targetType) {
  return getTargetTypeDef(targetType)?.concern === 'security';
}

export function isTargetDefinitionsConfirmed(targetType) {
  const def = getTargetTypeDef(targetType);
  return Boolean(def?.definitionsConfirmed);
}

export function getReasonsForTarget(targetType) {
  return REASONS_BY_TARGET[targetType] || [];
}

export function getIssuesForTarget(targetType, { includeUnconfirmed = false } = {}) {
  const issues = ISSUES_BY_TARGET[targetType] || [];
  if (includeUnconfirmed) return issues;
  return issues.filter((i) => i.definitionsConfirmed !== false);
}

export function getEligibilityText(flagSource, targetType) {
  if (!flagSource) return '—';
  if (flagSource === 'user_flag') return ELIGIBILITY_BY_SCOPE.user_flag;
  if (isSecurityTarget(targetType)) return ELIGIBILITY_BY_SCOPE.system_flag_security;
  return ELIGIBILITY_BY_SCOPE.system_flag_content;
}

export function getEvidenceRequirementText(flagSource, targetType) {
  if (!flagSource) return '—';
  if (flagSource === 'user_flag') return EVIDENCE_BY_SCOPE.user_flag;
  if (isSecurityTarget(targetType)) return EVIDENCE_BY_SCOPE.system_flag_security;
  return EVIDENCE_BY_SCOPE.system_flag_content;
}

export function getContextCaptureText(targetType) {
  return CONTEXT_CAPTURE_BY_TARGET[targetType] || '—';
}

export function supportsBlockSelected(targetType) {
  return Boolean(getTargetTypeDef(targetType)?.blockSelected);
}

export function getAvailableConditionFields(form) {
  const flagSource = form?.flagSource;
  const targetType = form?.targetType;
  if (!flagSource || !targetType) return [];

  const fields = [];
  if (flagSource === 'user_flag') {
    if (getReasonsForTarget(targetType).length) {
      fields.push({ value: 'reason', label: 'Reason' });
    }
    fields.push({ value: 'submitter_type', label: 'Submitter Type' });
    if (supportsBlockSelected(targetType)) {
      fields.push({ value: 'block_selected', label: 'Block Selected' });
    }
  } else if (flagSource === 'system_flag') {
    const issues = getIssuesForTarget(targetType, { includeUnconfirmed: true });
    if (issues.some((i) => i.definitionsConfirmed !== false)) {
      fields.push({ value: 'issue', label: 'Issue' });
    }
    if (!isSecurityTarget(targetType)) {
      fields.push({ value: 'match_classification', label: 'Match Classification' });
      if (CONTENT_AREA_OPTIONS.some((o) => (o.targetTypes || []).includes(targetType))) {
        fields.push({ value: 'content_area', label: 'Content Area' });
      }
      if (CONTENT_CONTEXT_OPTIONS.some((o) => (o.targetTypes || []).includes(targetType))) {
        fields.push({ value: 'content_context', label: 'Content Context' });
      }
      if (CHECK_CONTEXT_OPTIONS.some((o) => (o.targetTypes || []).includes(targetType))) {
        fields.push({ value: 'check_context', label: 'Check Context' });
      }
    }
  }
  return fields;
}

export function getConditionFieldLabel(field) {
  const all = [
    { value: 'reason', label: 'Reason' },
    { value: 'submitter_type', label: 'Submitter Type' },
    { value: 'block_selected', label: 'Block Selected' },
    { value: 'issue', label: 'Issue' },
    { value: 'match_classification', label: 'Match Classification' },
    { value: 'content_area', label: 'Content Area' },
    { value: 'content_context', label: 'Content Context' },
    { value: 'check_context', label: 'Check Context' },
  ];
  return labelFromOptions(all, field);
}

export function getOperatorsForField(field) {
  if (field === 'block_selected') {
    return CONDITION_OPERATOR_OPTIONS.filter((o) => o.value === 'equals' || o.value === 'does_not_equal');
  }
  return CONDITION_OPERATOR_OPTIONS;
}

export function getConditionValueOptions(field, form) {
  const targetType = form?.targetType;
  switch (field) {
    case 'reason':
      return getReasonsForTarget(targetType);
    case 'submitter_type':
      return SUBMITTER_TYPE_OPTIONS;
    case 'block_selected':
      return BLOCK_SELECTED_OPTIONS;
    case 'issue':
      return getIssuesForTarget(targetType, { includeUnconfirmed: false });
    case 'match_classification':
      return MATCH_CLASSIFICATION_OPTIONS;
    case 'content_area':
      return CONTENT_AREA_OPTIONS.filter((o) => (o.targetTypes || []).includes(targetType));
    case 'content_context':
      return CONTENT_CONTEXT_OPTIONS.filter((o) => (o.targetTypes || []).includes(targetType));
    case 'check_context':
      return CHECK_CONTEXT_OPTIONS.filter((o) => (o.targetTypes || []).includes(targetType));
    default:
      return [];
  }
}

export function getConditionValueLabel(field, value, form, condition = null) {
  const live = labelFromOptions(getConditionValueOptions(field, form), value);
  if (live) return live;
  const stored = condition?.valueLabels?.[value];
  if (stored) return stored;
  return String(value || '');
}

export function isConditionValueAvailable(field, value, form) {
  return getConditionValueOptions(field, form).some((o) => o.value === value);
}

export function formatConditionValueDisplay(field, value, form, condition = null) {
  const label = getConditionValueLabel(field, value, form, condition);
  if (!value) return '—';
  if (!isConditionValueAvailable(field, value, form)) {
    return `${label} (unavailable)`;
  }
  return label;
}

export function attachConditionValueLabels(condition, form) {
  const valueLabels = { ...(condition?.valueLabels || {}) };
  (condition?.values || []).forEach((value) => {
    if (value == null || value === '') return;
    const key = String(value);
    valueLabels[key] = getConditionValueLabel(condition.field, key, form, condition) || key;
  });
  return { ...condition, valueLabels };
}

/** Conditions that only restate fixed scope/eligibility (no narrowing). */
export function isRepeatedFixedRequirement(form, condition) {
  if (!condition?.field || !condition?.operator) return false;
  const values = [...new Set((condition.values || []).filter(Boolean).map(String))];
  if (!values.length) return false;

  // Field no longer offered for this scope (fixed by Flag Source / Module / Target Type).
  const offered = getAvailableConditionFields(form).map((o) => o.value);
  if (!offered.includes(condition.field)) return true;

  const universe = getConditionValueOptions(condition.field, form).map((o) => o.value);
  if (!universe.length) return false;

  // Equals / Is Any Of that selects every permitted value does not narrow coverage.
  if (condition.operator === 'equals' || condition.operator === 'is_any_of') {
    const selected = new Set(values);
    if (universe.length && universe.every((value) => selected.has(value)) && selected.size === universe.length) {
      return true;
    }
  }

  // Single-value universe with a positive match to that value.
  if (
    universe.length === 1
    && (condition.operator === 'equals' || condition.operator === 'is_any_of')
    && values.length === 1
    && values[0] === universe[0]
  ) {
    return true;
  }

  return false;
}

export function buildFlagScopeKey(form) {
  return [form?.flagSource || '', form?.module || '', form?.targetType || ''].join('|');
}

export function nextFlagRuleId(rules) {
  const nums = (rules || []).map((rule) => {
    const match = String(rule.id || '').match(/^FLG-(\d+)$/i);
    return match ? parseInt(match[1], 10) : 0;
  });
  const max = nums.length ? Math.max(...nums) : 0;
  return `FLG-${String(max + 1).padStart(4, '0')}`;
}

export function createEmptyCondition() {
  return { id: `cond-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, field: '', operator: '', values: [] };
}

export function createEmptyFlagForm() {
  return {
    id: '',
    ruleName: '',
    description: '',
    flagSource: '',
    module: '',
    targetType: '',
    severity: '',
    caseModule: '',
    additionalDetails: '',
    conditions: [],
    changeReason: '',
    dateAdded: '',
    addedBy: '',
    lastUpdated: '',
    lastUpdatedBy: '',
  };
}

function normalizeCondition(raw) {
  const values = Array.isArray(raw?.values)
    ? raw.values.filter((v) => v != null && String(v).trim() !== '').map(String)
    : raw?.value != null && String(raw.value).trim() !== ''
      ? [String(raw.value)]
      : [];
  const valueLabels = raw?.valueLabels && typeof raw.valueLabels === 'object' ? { ...raw.valueLabels } : {};
  return {
    id: raw?.id || `cond-${Date.now()}`,
    field: raw?.field || '',
    operator: raw?.operator || '',
    values,
    valueLabels,
  };
}

export function normalizeFlagForm(raw = {}) {
  const flagSource = raw.flagSource || '';
  const module = raw.module || '';
  const targetType = raw.targetType || '';
  const permittedCase = getPermittedCaseModule(flagSource, targetType);
  const isUser = flagSource === 'user_flag';
  return {
    id: raw.id || '',
    ruleName: raw.ruleName || '',
    description: raw.description || '',
    flagSource,
    module,
    targetType,
    severity: raw.severity || '',
    caseModule: raw.caseModule || permittedCase || '',
    additionalDetails: isUser ? raw.additionalDetails || '' : '',
    conditions: Array.isArray(raw.conditions) ? raw.conditions.map(normalizeCondition) : [],
    changeReason: raw.changeReason || '',
    dateAdded: raw.dateAdded || raw.createdOn || '',
    addedBy: raw.addedBy || raw.createdBy || '',
    lastUpdated: raw.lastUpdated || raw.modifiedOn || '',
    lastUpdatedBy: raw.lastUpdatedBy || raw.modifiedBy || '',
    version: raw.version,
    pendingDraft: raw.pendingDraft,
    configurationHistory: raw.configurationHistory,
    testHistory: raw.testHistory,
  };
}

export function applyDerivedFlagFields(form) {
  const next = { ...form };
  const permitted = getPermittedCaseModule(next.flagSource, next.targetType);
  if (permitted) next.caseModule = permitted;
  if (next.flagSource !== 'user_flag') next.additionalDetails = '';
  return next;
}

export function isAdditionalDetailsLockedToRequired(form) {
  if (form?.flagSource !== 'user_flag') return false;
  const conditions = form.conditions || [];
  if (!conditions.length) return false;
  const reasonConditions = conditions.filter((c) => c.field === 'reason' && c.operator && (c.values || []).length);
  if (!reasonConditions.length) return false;

  const reasons = getReasonsForTarget(form.targetType).map((r) => r.value);
  let allowed = new Set(reasons);
  reasonConditions.forEach((condition) => {
    const values = condition.values || [];
    if (condition.operator === 'equals' || condition.operator === 'is_any_of') {
      allowed = new Set([...allowed].filter((v) => values.includes(v)));
    } else if (condition.operator === 'does_not_equal' || condition.operator === 'is_not_any_of') {
      allowed = new Set([...allowed].filter((v) => !values.includes(v)));
    }
  });
  return allowed.size > 0 && [...allowed].every((v) => v === 'other');
}

function formatConditionLine(condition, form) {
  const field = getConditionFieldLabel(condition.field) || condition.field || '—';
  const operator = getConditionOperatorLabel(condition.operator) || condition.operator || '—';
  const values = (condition.values || []).map((v) => formatConditionValueDisplay(condition.field, v, form, condition));
  const valueText = values.length ? values.join(', ') : '—';
  return `${field} · ${operator} · ${valueText}`;
}

export function formatConditionsPreview(conditions = [], form = {}) {
  if (!conditions.length) return { text: 'No Conditions', extraCount: 0, lines: [] };
  const lines = conditions.map((c) => formatConditionLine(c, form));
  if (lines.length === 1) return { text: lines[0], extraCount: 0, lines };
  return {
    text: `${lines[0]} · +${lines.length - 1} more`,
    extraCount: lines.length - 1,
    lines,
  };
}

export function formatFlagDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const day = date.getDate();
  const month = date.toLocaleString('en-GB', { month: 'short' });
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const suffix = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12;
  return `${day} ${month} ${year}, ${hours}:${minutes} ${suffix}`;
}

export function sortFlagRules(rules = []) {
  return [...rules].sort((a, b) => {
    const aTime = new Date(a.lastUpdated || a.dateAdded || 0).getTime();
    const bTime = new Date(b.lastUpdated || b.dateAdded || 0).getTime();
    return bTime - aTime;
  });
}

function actorFields(iso, name) {
  return {
    dateAdded: iso,
    addedBy: name,
    lastUpdated: iso,
    lastUpdatedBy: name,
  };
}

/** Spec example row plus a small set of content Flag Rules. Security scopes are not sample-saved. */
export function buildSampleFlagRules() {
  return [
    {
      id: 'FLG-0001',
      ruleName: 'Post Flag Details',
      description: 'Defines Flag requirements for Posts reported with Other.',
      flagSource: 'user_flag',
      module: 'posts',
      targetType: 'post',
      severity: 'medium',
      caseModule: 'moderation',
      additionalDetails: 'required',
      conditions: [
        { id: 'cond-sample-1', field: 'reason', operator: 'equals', values: ['other'] },
      ],
      changeReason: 'Added the Flag requirements for Other on Posts.',
      ...actorFields('2026-09-12T10:15:00+10:00', 'Olivia Brown'),
    },
    {
      id: 'FLG-0002',
      ruleName: 'Message Abuse Flags',
      description: 'User Flag requirements for received message threads reported for abuse.',
      flagSource: 'user_flag',
      module: 'messages',
      targetType: 'message_thread',
      severity: 'high',
      caseModule: 'moderation',
      additionalDetails: 'optional',
      conditions: [
        { id: 'cond-sample-2', field: 'reason', operator: 'equals', values: ['abuse'] },
      ],
      changeReason: 'Set High severity for message abuse reports.',
      ...actorFields('2026-09-14T14:40:00+10:00', 'Alex Morgan'),
    },
    {
      id: 'FLG-0003',
      ruleName: 'System Post Spam Match',
      description: 'System Flag for confirmed spam matches on published Posts.',
      flagSource: 'system_flag',
      module: 'posts',
      targetType: 'post',
      severity: 'medium',
      caseModule: 'moderation',
      additionalDetails: '',
      conditions: [
        { id: 'cond-sample-3a', field: 'issue', operator: 'equals', values: ['spam_or_unwanted_promotion'] },
        { id: 'cond-sample-3b', field: 'match_classification', operator: 'equals', values: ['confirmed_match'] },
      ],
      changeReason: 'Linked confirmed spam findings on Posts to Moderation.',
      ...actorFields('2026-09-18T09:05:00+10:00', 'Olivia Brown'),
    },
    {
      id: 'FLG-0004',
      ruleName: 'Review Integrity System Flag',
      description: 'System Flag for review integrity concerns on public reviews or replies.',
      flagSource: 'system_flag',
      module: 'reviews',
      targetType: 'public_review_or_reply',
      severity: 'high',
      caseModule: 'moderation',
      additionalDetails: '',
      conditions: [
        { id: 'cond-sample-4', field: 'issue', operator: 'equals', values: ['review_integrity_concern'] },
      ],
      changeReason: 'Created Flag coverage for review integrity findings.',
      ...actorFields('2026-09-20T16:20:00+10:00', 'Jordan Lee'),
    },
  ].map(normalizeFlagForm);
}

export const FLAG_FILTER_ALL = 'All';

export const FLAG_SOURCE_FILTER_OPTIONS = [
  { value: FLAG_FILTER_ALL, label: 'All Flag Sources' },
  ...FLAG_SOURCE_OPTIONS.map((o) => ({ value: o.label, label: o.label })),
];
export const FLAG_MODULE_FILTER_OPTIONS = [
  { value: FLAG_FILTER_ALL, label: 'All Modules' },
  ...FLAG_MODULE_OPTIONS.filter((m) => m.value !== 'accounts').map((o) => ({ value: o.label, label: o.label })),
];
export const FLAG_CASE_MODULE_FILTER_OPTIONS = [
  { value: FLAG_FILTER_ALL, label: 'All Case Modules' },
  ...CASE_MODULE_OPTIONS.map((o) => ({ value: o.label, label: o.label })),
];
export const FLAG_SEVERITY_FILTER_OPTIONS = [
  { value: FLAG_FILTER_ALL, label: 'All Severities' },
  ...SEVERITY_OPTIONS.map((o) => ({ value: o.label, label: o.label })),
];
